import * as React from 'react'
import {StyleSheet, View} from 'react-native'
// Based on @react-navigation/native-stack/src/navigators/createNativeStackNavigator.ts
// MIT License
// Copyright (c) 2017 React Navigation Contributors
import {
  createNavigatorFactory,
  type EventArg,
  type NavigatorTypeBagBase,
  type ParamListBase,
  type StackActionHelpers,
  StackActions,
  type StackNavigationState,
  StackRouter,
  type StackRouterOptions,
  type StaticConfig,
  type TypedNavigator,
  useNavigationBuilder,
} from '@react-navigation/native'
import {NativeStackView} from '@react-navigation/native-stack'
import {
  type NativeStackNavigationEventMap,
  type NativeStackNavigationOptions,
  type NativeStackNavigationProp,
  type NativeStackNavigatorProps,
} from '@react-navigation/native-stack'

import {PWI_ENABLED} from '#/lib/build-flags'
import {useWebMediaQueries} from '#/lib/hooks/useWebMediaQueries'
import {isNative, isWeb} from '#/platform/detection'
import {useSession} from '#/state/session'
import {useOnboardingState} from '#/state/shell'
import {
  useLoggedOutView,
  useLoggedOutViewControls,
} from '#/state/shell/logged-out'
import {LoggedOut} from '#/view/com/auth/LoggedOut'
import {ActivateTellusScreen} from '#/screens/ActivateTellusScreen'
import {Deactivated} from '#/screens/Deactivated'
import {Onboarding} from '#/screens/Onboarding'
import {SignupQueued} from '#/screens/SignupQueued'
import {Takendown} from '#/screens/Takendown'
import {atoms as a, useLayoutBreakpoints, web} from '#/alf'
import {PolicyUpdateOverlay} from '#/components/PolicyUpdateOverlay'
import {BottomBarWeb} from './bottom-bar/BottomBarWeb'
import {DesktopLeftNav} from './desktop/LeftNav'
import {DesktopRightNav} from './desktop/RightNav'

type NativeStackNavigationOptionsWithAuth = NativeStackNavigationOptions & {
  requireAuth?: boolean
  hideRightRail?: boolean
  hideChrome?: boolean
  mainContentMaxWidth?: number
  mainContentFullWidth?: boolean
}

function NativeStackNavigator({
  id,
  initialRouteName,
  children,
  layout,
  screenListeners,
  screenOptions,
  screenLayout,
  ...rest
}: NativeStackNavigatorProps) {
  // --- this is copy and pasted from the original native stack navigator ---
  const {state, describe, descriptors, navigation, NavigationContent} =
    useNavigationBuilder<
      StackNavigationState<ParamListBase>,
      StackRouterOptions,
      StackActionHelpers<ParamListBase>,
      NativeStackNavigationOptionsWithAuth,
      NativeStackNavigationEventMap
    >(StackRouter, {
      id,
      initialRouteName,
      children,
      layout,
      screenListeners,
      screenOptions,
      screenLayout,
    })

  React.useEffect(
    () =>
      // @ts-expect-error: there may not be a tab navigator in parent
      navigation?.addListener?.('tabPress', (e: any) => {
        const isFocused = navigation.isFocused()

        // Run the operation in the next frame so we're sure all listeners have been run
        // This is necessary to know if preventDefault() has been called
        requestAnimationFrame(() => {
          if (
            state.index > 0 &&
            isFocused &&
            !(e as EventArg<'tabPress', true>).defaultPrevented
          ) {
            // When user taps on already focused tab and we're inside the tab,
            // reset the stack to replicate native behaviour
            navigation.dispatch({
              ...StackActions.popToTop(),
              target: state.key,
            })
          }
        })
      }),
    [navigation, state.index, state.key],
  )

  // --- our custom logic starts here ---
  const {hasSession, currentAccount} = useSession()
  const activeRoute = state.routes[state.index]
  const activeDescriptor = descriptors[activeRoute.key]
  const activeOptions =
    activeDescriptor.options as NativeStackNavigationOptionsWithAuth
  const activeRouteRequiresAuth = activeOptions.requireAuth ?? false
  const onboardingState = useOnboardingState()
  const {showLoggedOut} = useLoggedOutView()
  const {setShowLoggedOut} = useLoggedOutViewControls()
  const {isMobile, isDesktop} = useWebMediaQueries()
  const {leftNavMinimal} = useLayoutBreakpoints()
  if (hasSession && currentAccount?.signupQueued) {
    return <SignupQueued />
  }
  if (hasSession && currentAccount?.status === 'takendown') {
    return <Takendown />
  }
  if (showLoggedOut) {
    return <LoggedOut onDismiss={() => setShowLoggedOut(false)} />
  }
  if (!hasSession && (!PWI_ENABLED || activeRouteRequiresAuth || isNative)) {
    return <ActivateTellusScreen />
  }
  if (currentAccount?.status === 'deactivated') {
    return <Deactivated />
  }
  // Activation gate disabled to avoid blocking /messages.
  // The chat screen handles its own auth/session.
  //   if (hasSession && !isActivationComplete) {
  //     return <ActivateTellusScreen />
  //   }
  if (onboardingState.isActive) {
    return <Onboarding />
  }
  const newDescriptors: typeof descriptors = {}
  for (let key in descriptors) {
    const descriptor = descriptors[key]
    const requireAuth = descriptor.options.requireAuth ?? false
    newDescriptors[key] = {
      ...descriptor,
      render() {
        if (requireAuth && !hasSession) {
          return <View />
        } else {
          return descriptor.render()
        }
      },
    }
  }

  const isIPhoneWebLayout =
    isWeb &&
    typeof navigator !== 'undefined' &&
    /iP(hone|od)/i.test(navigator.userAgent || '')
  const isMobileLayout = isMobile || isIPhoneWebLayout
  const showBottomBar = isMobileLayout
  const showLeftNav = !isMobileLayout
  const showRightNav =
    isDesktop && !isMobileLayout && !activeOptions.hideRightRail
  const mainContentStyle = [
    styles.mainContent,
    activeOptions.mainContentMaxWidth != null
      ? {maxWidth: activeOptions.mainContentMaxWidth}
      : null,
    activeOptions.mainContentFullWidth ? styles.mainContentFullWidth : null,
    isMobileLayout &&
      web({
        paddingHorizontal: 0,
        paddingBottom: 'var(--tabbar-h)',
      }),
  ]

  return (
    <NavigationContent>
      {isWeb ? (
        <>
          <View style={styles.webShell}>
            {showLeftNav ? (
              <View
                style={[
                  styles.leftColumn,
                  leftNavMinimal && styles.leftColumnMinimal,
                ]}>
                <DesktopLeftNav hideChrome={activeOptions.hideChrome} />
              </View>
            ) : null}

            <View style={styles.mainColumn}>
              <View role="main" style={mainContentStyle}>
                <NativeStackView
                  {...rest}
                  state={state}
                  navigation={navigation}
                  descriptors={descriptors}
                  describe={describe}
                />
              </View>
            </View>

            {showRightNav ? (
              <View style={styles.rightColumn}>
                <DesktopRightNav routeName={activeRoute.name} />
              </View>
            ) : null}
          </View>
          {showBottomBar ? <BottomBarWeb /> : null}
        </>
      ) : (
        <View role="main" style={a.flex_1}>
          <NativeStackView
            {...rest}
            state={state}
            navigation={navigation}
            descriptors={descriptors}
            describe={describe}
          />
        </View>
      )}

      {/* Only shown after logged in and onboaring etc are complete */}
      {hasSession && <PolicyUpdateOverlay />}
    </NavigationContent>
  )
}

const styles = StyleSheet.create({
  webShell: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'stretch',
    width: '100%',
    minHeight: '100dvh',
  },
  leftColumn: {
    flexShrink: 0,
    width: 240,
  },
  leftColumnMinimal: {
    width: 86,
  },
  mainColumn: {
    flex: 1,
    minWidth: 0,
  },
  mainContent: {
    flex: 1,
    minWidth: 0,
    width: '100%',
    maxWidth: 900,
    alignSelf: 'center',
    paddingHorizontal: 16,
  },
  mainContentFullWidth: {
    maxWidth: '100%',
    alignSelf: 'flex-start',
    paddingHorizontal: 0,
  },
  rightColumn: {
    flexShrink: 0,
  },
})

export function createNativeStackNavigatorWithAuth<
  const ParamList extends ParamListBase,
  const NavigatorID extends string | undefined = undefined,
  const TypeBag extends NavigatorTypeBagBase = {
    ParamList: ParamList
    NavigatorID: NavigatorID
    State: StackNavigationState<ParamList>
    ScreenOptions: NativeStackNavigationOptionsWithAuth
    EventMap: NativeStackNavigationEventMap
    NavigationList: {
      [RouteName in keyof ParamList]: NativeStackNavigationProp<
        ParamList,
        RouteName,
        NavigatorID
      >
    }
    Navigator: typeof NativeStackNavigator
  },
  const Config extends StaticConfig<TypeBag> = StaticConfig<TypeBag>,
>(config?: Config): TypedNavigator<TypeBag, Config> {
  return createNavigatorFactory(NativeStackNavigator)(config)
}
